// src/components/GeneratedSalesOrder/index.jsx
import { Box, Grid, Tooltip, IconButton, Typography, Button, Drawer, Divider, Badge, List, ListItemButton, ListItemText, Alert } from '@mui/material';
import { useCallback, useEffect, useState, useMemo, useRef, useLayoutEffect } from 'react';
import axios from 'axios';
import Header from './Header';
import styles from './index.module.css';
import ProductDetails from './Presentation';
import DocumentSectionTitle from './DocumentSectionTitle';
import { Container } from '@mui/material';
import Footer from './Footer';
import ArtworkProofPackagePanel from './ArtworkProofPackagePanel';
import { useProjectTemplateConfig } from '../../hooks/useProjectTemplateConfig';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import {
  fetchEstimationDetails,
  preflightGeneratedSalesOrderArtwork,
  submitPublicSalesOrderAction,
  updateComment,
  updateEstimation,
} from '../../services/estimation.js';
import { mergeGeneratedArtworkPreviews } from './generatedArtworkPreviewMetadata';
import {
  updateProductsToOrder,
  regenerateEstimateFromCart,
  regenerateSalesOrderFromCart,
  resetSalesOrderApproval,
  fetchCartSnapshots,
  fetchDocumentArtworkProofs,
} from '../../services/orders';
import { createInvoice, fetchInvoicesListByOrder, isInvoicePaidStatus } from '../../services/invoices';
import EstimationDetails from './EstimationDetails.js/index.js';
import TotalContainer from './TotalContainer/index.js';
import EstimationLoading from '../EstimationLoader/index.js';
import { Toaster, toast } from 'react-hot-toast';
import EditIcon from '@mui/icons-material/Edit';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import GeneratedDocumentPrintActions from './GeneratedDocumentPrintActions';
import { computeTotalsFromSO, fromCents, isCustomerFacingVisibleLine } from '../../utils/totals';
import { resolveOtherServiceCustomerAmounts } from '../../utils/otherServicePricing';
import {
  attachProofReplacementsToProducts,
  normalizeMaybeId,
} from './proofMatching';
import {
  findGeneratedEstimateProductById,
  resolveGeneratedEstimateLineId,
  resolveGeneratedEstimateOrderItemId,
  resolveGeneratedEstimateProductIdentityKey,
  resolveGeneratedEstimateProductId,
  updateGeneratedEstimateProductComment,
} from './productIdentity';
import { resolveCustomerDocumentLayoutRows } from '../customerDocumentLayout';
import RegenerationLoader from './RegenerationLoader';
import RegenerationRequired from './RegenerationRequired';
import ApprovalRegenerationDialog from './ApprovalRegenerationDialog';
import DecorationRoutingErrorDialog, { getDecorationRoutingError } from './DecorationRoutingErrorDialog';
import ResetSalesOrderApprovalDialog from './ResetSalesOrderApprovalDialog';
import {
  canInternalUserRegenerateGeneratedDocument,
  canInternalUserResetSalesOrderApproval,
} from './documentRegenerationPermissions';
import { getServiceDescription } from '../PricingTable/serviceLabel';


const getSalesOrderOtherServices = (salesOrder = {}) => {
  const cartSnapshot = salesOrder?.cartSnapshot || salesOrder?.stampedBookedAmount?.cartSnapshot || {};
  const payload = cartSnapshot?.payload || {};
  const candidates = [
    payload?.OtherService,
    payload?.otherServices,
    cartSnapshot?.OtherService,
    cartSnapshot?.otherServices,
    salesOrder?.OtherService,
    salesOrder?.otherServices,
    salesOrder?.otherService,
  ];
  return candidates.find((value) => Array.isArray(value)) || [];
};

const getBaseStatus = (status) => String(status || '').split('_')[0];
const isEstimateStatus = (status) => getBaseStatus(status) === '1';
const isAfterEstimateStatus = (status) => Number(getBaseStatus(status)) > 1;
const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toLowerCase();
const hasUsableAccessToken = () => {
  try {
    const token = String(localStorage.getItem('accessToken') || '').trim();
    if (!token) return false;

    const lowered = token.toLowerCase();
    if (
      lowered === 'null' ||
      lowered === 'undefined' ||
      lowered === 'bearer null' ||
      lowered === 'bearer undefined'
    ) {
      return false;
    }

    const rawToken = token.startsWith('Bearer ') ? token.slice(7).trim() : token;
    const parts = rawToken.split('.');
    if (parts.length !== 3) return false;
    if (!parts[0] || !parts[1] || !parts[2]) return false;

    return true;
  } catch (_) {
    return false;
  }
};

const getProofApprovalSummaryFromRows = (rows = []) => {
  const summary = {
    total: 0,
    approved: 0,
    rejected: 0,
    pending: 0,
    canProceed: true,
    canSign: true,
    blockingReason: null,
    firstBlockingMappingId: null,
    firstBlockingProofId: null,
  };

  const list = Array.isArray(rows) ? rows : [];
  summary.total = list.length;
  if (!list.length) {
    return summary;
  }

  list.forEach((row) => {
    const proof = row?.proof || {};
    const userState = String(proof?.approvals?.user?.state || 'pending').toLowerCase();
    const clientState = String(proof?.approvals?.client?.state || 'pending').toLowerCase();
    const status = String(proof?.status || proof?.proofStatus || '').toLowerCase();
    const rejected = userState === 'rejected' || clientState === 'rejected' || status === 'rejected';
    const approved =
      status === 'approved' ||
      (userState === 'approved' && clientState === 'approved');

    if (rejected) {
      summary.rejected += 1;
      if (summary.blockingReason !== 'rejected') {
        summary.blockingReason = 'rejected';
        summary.firstBlockingMappingId = row?.mapping?.id ?? null;
        summary.firstBlockingProofId = row?.proof?.id ?? null;
      }
      return;
    }

    if (approved) {
      summary.approved += 1;
      return;
    }

    summary.pending += 1;
    if (!summary.blockingReason) {
      summary.blockingReason = 'pending';
      summary.firstBlockingMappingId = row?.mapping?.id ?? null;
      summary.firstBlockingProofId = row?.proof?.id ?? null;
    }
  });

  summary.canProceed = summary.rejected === 0 && summary.pending === 0;
  summary.canSign = summary.canProceed;
  return summary;
};

const isProofRowApproved = (row) => {
  const proof = row?.proof || {};
  const proofStatus = String(proof?.proofStatus || proof?.status || row?.proofStatus || row?.status || "").toLowerCase();
  const userState = String(proof?.approvals?.user?.state || "").toLowerCase();
  const clientState = String(proof?.approvals?.client?.state || "").toLowerCase();
  return proofStatus === "approved" || (userState === "approved" && clientState === "approved");
};

const isProofRowRejected = (row) => {
  const proof = row?.proof || {};
  const proofStatus = String(proof?.proofStatus || proof?.status || row?.proofStatus || row?.status || "").toLowerCase();
  const userState = String(proof?.approvals?.user?.state || "").toLowerCase();
  const clientState = String(proof?.approvals?.client?.state || "").toLowerCase();
  return proofStatus === "rejected" || userState === "rejected" || clientState === "rejected";
};

const findNextActionableProofMappingId = (rows, currentMappingId) => {
  const list = Array.isArray(rows) ? rows : [];
  const currentIndex = list.findIndex((row) => Number(row?.mapping?.id) === Number(currentMappingId));
  const isActionable = (row) => {
    const mappingId = row?.mapping?.id;
    if (mappingId == null) return false;
    return !isProofRowApproved(row) && !isProofRowRejected(row);
  };

  const after = currentIndex >= 0 ? list.slice(currentIndex + 1).find(isActionable) : null;
  if (after) return after.mapping.id;

  const anywhere = list.find(isActionable);
  return anywhere?.mapping?.id ?? null;
};

const createInitialGeneratedSalesOrderRenderState = ({ id, documentType }) => ({
  requestStarted: false,
  requestCompleted: false,
  hasDocument: false,
  dataLoaded: false,
  error: null,
  renderedSnapshotId: null,
  renderedSnapshotRevision: null,
  documentType,
  documentId: id || null,
});

const publishGeneratedSalesOrderRenderState = (nextState) => {
  if (typeof window === 'undefined') return;
  window.__GENERATED_SALES_ORDER_RENDER_STATE__ = nextState;
};

const publishGeneratedSalesOrderMountState = (nextState) => {
  if (typeof window === 'undefined') return;
  window.__GENERATED_SALES_ORDER_MOUNTED__ = nextState;
};

const createGeneratedSalesOrderDocumentState = ({
  id,
  documentType,
  estimationDetails = null,
  renderedLayoutRows = [],
  explicitSnapshotId = null,
  expectedSnapshotRevision = null,
  requestCompleted = false,
  error = null,
}) => {
  const documentId = estimationDetails?.estimationId || id || null;
  const renderedSnapshotId =
    estimationDetails?.renderedSnapshotId ||
    estimationDetails?.snapshotId ||
    estimationDetails?.cartSnapshotId ||
    null;
  const renderedSnapshotRevision =
    estimationDetails?.renderedSnapshotRevision ||
    estimationDetails?.snapshotRevision ||
    estimationDetails?.resolvedRevisionNumber ||
    null;
  const productCount = Array.isArray(estimationDetails?.products) ? estimationDetails.products.length : 0;
  const layoutRowCount = Array.isArray(renderedLayoutRows) ? renderedLayoutRows.length : 0;
  const snapshotMatches = explicitSnapshotId
    ? String(renderedSnapshotId || '') === String(explicitSnapshotId)
    : true;
  const revisionMatches = expectedSnapshotRevision != null
    ? Number(renderedSnapshotRevision || 0) === Number(expectedSnapshotRevision)
    : true;
  const hasDocumentData =
    Boolean(estimationDetails) &&
    estimationDetails?.documentRequiresRegeneration !== true &&
    (productCount > 0 || layoutRowCount > 0);

  return {
    documentId,
    documentType,
    requestCompleted: requestCompleted === true,
    hasDocumentData,
    error: error || null,
    productCount,
    layoutRowCount,
    renderedSnapshotId,
    renderedSnapshotRevision,
    snapshotMatches,
    revisionMatches,
    ready:
      requestCompleted === true &&
      !error &&
      hasDocumentData &&
      snapshotMatches &&
      revisionMatches,
  };
};

const publishGeneratedSalesOrderDocumentState = (nextState) => {
  if (typeof window === 'undefined') return;
  window.__GENERATED_SALES_ORDER_DOCUMENT_STATE__ = nextState;
};

const GeneratedSalesOrder = () => {
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const isEstimatePage = location.pathname.startsWith('/generated-estimate');
  const documentType = isEstimatePage ? 'estimate' : 'sales-order';
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isRenderMode = useMemo(() => searchParams.has('render'), [searchParams]);

  // One print presentation for the browser Print button, Ctrl+P and
  // background PDF snapshots (?render=...). The document contents still come
  // from the requested (possibly frozen) Sales Order snapshot.
  useLayoutEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const printClass = 'elite-generated-document-manual-print';
    const style = document.createElement('style');
    style.setAttribute('data-generated-document-native-page', 'true');
    style.textContent = '@page { size: Letter portrait; margin: 0 !important; }';
    document.body.classList.add(printClass);
    document.head.appendChild(style);
    return () => {
      document.body.classList.remove(printClass);
      style.remove();
    };
  }, []);

  const explicitSnapshotRevision = searchParams.get('snapshotRevision');
  const explicitSnapshotId = searchParams.get('snapshotId');
  const expectedSnapshotRevision = explicitSnapshotRevision != null && explicitSnapshotRevision !== ''
    ? Number(explicitSnapshotRevision)
    : null;
  const [loading, setLoading] = useState(false);
  const [estimationDetails, setEstimationDetails] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [commentStateByProduct, setCommentStateByProduct] = useState({});
  const commentPendingRef = useRef({});
  const confirmedCommentRef = useRef({});
  const [instanceUniqueId, setInstanceUniqueId] = useState(null);
  const [cartOrder, setCartOrder] = useState(null);
  const [cartPanelOpen, setCartPanelOpen] = useState(false);
  const [proofBundle, setProofBundle] = useState(null);
  const [proofBundleLoading, setProofBundleLoading] = useState(false);
  const [proofBundleError, setProofBundleError] = useState('');

  const [snapshotHistoryOpen, setSnapshotHistoryOpen] = useState(false);
  const [generatedHistoryAllowed, setGeneratedHistoryAllowed] = useState(false);
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [snapshotHistoryLoading, setSnapshotHistoryLoading] = useState(false);
  const [snapshotHistoryError, setSnapshotHistoryError] = useState('');
  const [regeneratingDocument, setRegeneratingDocument] = useState(false);
  const regenerationInFlightRef = useRef(false);
  const [approvalRegeneration, setApprovalRegeneration] = useState({ open: false, mode: "reset" });
  const [decorationRoutingError, setDecorationRoutingError] = useState({ open: false, issues: [] });
  const [resetApprovalDialogOpen, setResetApprovalDialogOpen] = useState(false);
  const [resettingApproval, setResettingApproval] = useState(false);
  const [renderState, setRenderState] = useState(() =>
    createInitialGeneratedSalesOrderRenderState({ id, documentType })
  );
  const renderStateKey = `${documentType}:${id || ''}:${explicitSnapshotId || ''}:${explicitSnapshotRevision || ''}`;
  const mountLoggedRef = useRef(false);
  const hydratedLogKeyRef = useRef('');
  const readyLoggedRef = useRef(false);

  const hydrateGeneratedDocument = useCallback((salesOrder = {}) => {
    let products = Array.isArray(salesOrder?.products)
      ? salesOrder.products.map((p) => {
          const decorations = Array.isArray(p.decorations) && p.decorations.length
            ? [...p.decorations]
            : (Array.isArray(p.Decorations) ? [...p.Decorations] : []);

          const base =
            Array.isArray(p.ArtWorks) && p.ArtWorks.length
              ? [...p.ArtWorks]
              : Array.isArray(p.artworks)
                ? [...p.artworks]
                : [];

          const normalized = decorations.map((dec, idx) => {
            const fromBase = base[idx] || {};
            const fromDec = dec?.ArtWork ? dec.ArtWork : {};
            const firstPopulated = (...values) => values.find((value) => value !== null && value !== undefined && String(value).trim() !== '') || '';
            const Image = firstPopulated(fromDec.Image, fromDec.FileUrl, fromBase.Image, fromBase.FileUrl);

            return {
              ...fromBase,
              ...fromDec,
              Image,
              FileUrl: firstPopulated(fromDec.FileUrl, fromDec.Image, fromBase.FileUrl, fromBase.Image, Image),
              LogoName: firstPopulated(fromDec.LogoName, fromBase.LogoName),
              LogoSize: firstPopulated(fromDec.LogoSize, fromBase.LogoSize),
              LogoColor: firstPopulated(fromDec.LogoColor, fromBase.LogoColor),
              RepeatLogo: firstPopulated(fromDec.RepeatLogo, fromBase.RepeatLogo),
              ImprintType: firstPopulated(fromDec.ImprintType, fromBase.ImprintType, p?.Imprinting?.ImprintType),
              ProofRequired: firstPopulated(fromDec.ProofRequired, fromBase.ProofRequired),
              ProofRequiredAction: firstPopulated(fromDec.ProofRequiredAction, fromBase.ProofRequiredAction),
              SupplierNotes: firstPopulated(fromDec.SupplierNotes, fromBase.SupplierNotes),
              CustomerFileId: firstPopulated(fromDec.CustomerFileId, fromBase.CustomerFileId),
            };
          });

          return {
            ...p,
            decorations,
            ArtWorks: normalized,
          };
        })
      : [];

    const confirmedByServer = [];
    Object.entries(confirmedCommentRef.current).forEach(([productIdentityKey, confirmed]) => {
      const serverProduct = findGeneratedEstimateProductById(products, productIdentityKey);
      if (String(serverProduct?.comment ?? '') === String(confirmed?.comment ?? '')) {
        confirmedByServer.push(productIdentityKey);
      } else {
        products = updateGeneratedEstimateProductComment(products, productIdentityKey, confirmed?.comment ?? '');
      }
    });
    if (confirmedByServer.length) {
      setCommentStateByProduct((prev) => {
        const next = { ...prev };
        confirmedByServer.forEach((key) => {
          delete confirmedCommentRef.current[key];
          delete next[key];
        });
        return next;
      });
    }

    setEstimationDetails({
      ...salesOrder,
      products,
      layout: Array.isArray(salesOrder?.layout) ? salesOrder.layout : [],
      cartSections: Array.isArray(salesOrder?.cartSections) ? salesOrder.cartSections : [],
    });
  }, []);

  const getEstimation = async ({ silent = false } = {}) => {
    try {
      const shouldStaySilent = silent || regenerationInFlightRef.current;
      if (!shouldStaySilent) setLoading(true);
      setLoadError('');
      if (isRenderMode) {
        console.log('[generated-sales-order-render] fetch:start', {
          id,
          snapshotId: explicitSnapshotId || null,
          snapshotRevision: explicitSnapshotRevision || null,
        });
      }
      setRenderState((previous) => {
        const nextState = {
        ...createInitialGeneratedSalesOrderRenderState({ id, documentType }),
        requestStarted: true,
        requestCompleted: previous.requestCompleted && silent,
        };
        publishGeneratedSalesOrderRenderState(nextState);
        return nextState;
      });

      const response = await fetchEstimationDetails(id, {
        snapshotRevision: explicitSnapshotRevision,
        snapshotId: explicitSnapshotId,
        documentType,
      });

      const resolvedDocument = response?.salesOrder || {};
      const renderedSnapshotId =
        resolvedDocument?.renderedSnapshotId ||
        resolvedDocument?.snapshotId ||
        resolvedDocument?.cartSnapshotId ||
        null;
      const renderedSnapshotRevision =
        resolvedDocument?.renderedSnapshotRevision ||
        resolvedDocument?.snapshotRevision ||
        resolvedDocument?.resolvedRevisionNumber ||
        null;
      const hasDocument = Boolean(response?.salesOrder) && resolvedDocument?.documentRequiresRegeneration !== true;
      if (isRenderMode) {
        console.log('[generated-sales-order-render] fetch:resolved', {
          hasSalesOrder: Boolean(response?.salesOrder),
          estimationId: resolvedDocument?.estimationId || id,
          productCount: Array.isArray(resolvedDocument?.products) ? resolvedDocument.products.length : 0,
          renderedSnapshotId,
          renderedSnapshotRevision,
        });
      }

      hydrateGeneratedDocument(resolvedDocument);
      if (documentType === 'sales-order') {
        try {
          const previewResult = await preflightGeneratedSalesOrderArtwork(id);
          if (Array.isArray(previewResult?.previews) && previewResult.previews.length) {
            hydrateGeneratedDocument(
              mergeGeneratedArtworkPreviews(resolvedDocument, previewResult.previews)
            );
          }
        } catch (previewError) {
          // Artwork remains downloadable through the existing vector fallback.
          console.warn('[GeneratedSalesOrder] artwork preview preflight unavailable:', previewError?.message || previewError);
        }
      }
      const nextState = {
        requestStarted: true,
        requestCompleted: true,
        hasDocument,
        dataLoaded: Boolean(response?.salesOrder),
        error: null,
        renderedSnapshotId,
        renderedSnapshotRevision,
        documentType,
        documentId: resolvedDocument?.estimationId || id,
      };
      publishGeneratedSalesOrderRenderState(nextState);
      setRenderState(nextState);
    } catch (error) {
      console.error(error);
      const message = error?.message || 'Failed to load generated sales order.';
      if (isRenderMode) {
        console.log('[generated-sales-order-render] fetch:error', {
          message,
        });
      }
      setLoadError(message);
      const nextState = {
        ...createInitialGeneratedSalesOrderRenderState({ id, documentType }),
        requestStarted: true,
        requestCompleted: true,
        error: message,
      };
      publishGeneratedSalesOrderRenderState(nextState);
      setRenderState(nextState);
    } finally {
      if (!(silent || regenerationInFlightRef.current)) setLoading(false);
    }
  };

  useEffect(() => {
    setRenderState(createInitialGeneratedSalesOrderRenderState({ id, documentType }));
  }, [documentType, explicitSnapshotId, explicitSnapshotRevision, id]);

  useLayoutEffect(() => {
    const mountState = {
      mounted: true,
      id: id || null,
      pathname: location.pathname,
      search: location.search,
      isRenderMode,
      documentType,
      mountedAt: new Date().toISOString(),
    };
    publishGeneratedSalesOrderMountState(mountState);
    if (isRenderMode && !mountLoggedRef.current) {
      mountLoggedRef.current = true;
      console.log('[generated-sales-order] component:mounted', {
        id: mountState.id,
        pathname: mountState.pathname,
        search: mountState.search,
        isRenderMode: mountState.isRenderMode,
        documentType: mountState.documentType,
      });
    }
    return () => {
      publishGeneratedSalesOrderMountState({
        ...mountState,
        mounted: false,
        unmountedAt: new Date().toISOString(),
      });
    };
  }, [documentType, id, isRenderMode, location.pathname, location.search]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    window.__ELITE_CORE_API_BASE_URL__ =
      process.env.REACT_APP_NODEJS_SERVER_URL || '';
    publishGeneratedSalesOrderRenderState(
      createInitialGeneratedSalesOrderRenderState({ id, documentType })
    );
    publishGeneratedSalesOrderDocumentState(
      createGeneratedSalesOrderDocumentState({
        id,
        documentType,
        explicitSnapshotId,
        expectedSnapshotRevision,
      })
    );

    return () => {
      delete window.__GENERATED_SALES_ORDER_RENDER_STATE__;
      delete window.__GENERATED_SALES_ORDER_DOCUMENT_STATE__;
    };
  }, [renderStateKey, documentType, expectedSnapshotRevision, explicitSnapshotId, id]);

  useEffect(() => {
    publishGeneratedSalesOrderRenderState(renderState);
  }, [renderState]);

  // ─────────────────────────────────────────────────────────────
  // Comments gating logic (Estimate only + commentsEnabled)
  const statusCode =
    estimationDetails?.status !== undefined && estimationDetails?.status !== null
      ? String(estimationDetails.status)
      : '';
  const isEstimate = isEstimatePage || isEstimateStatus(statusCode);

  const commentsEnabledRaw = estimationDetails?.commentsEnabled;
  const commentsEnabledParsed =
    typeof commentsEnabledRaw === 'boolean'
      ? commentsEnabledRaw
      : commentsEnabledRaw === 1 || commentsEnabledRaw === '1' || commentsEnabledRaw === 'true';
  const commentsEnabled = commentsEnabledRaw === undefined ? true : Boolean(commentsEnabledParsed);

  const canShowComments = isEstimate && commentsEnabled;
  const renderedLayoutRows = useMemo(() => {
    return resolveCustomerDocumentLayoutRows({
      products: Array.isArray(estimationDetails?.products) ? estimationDetails.products : [],
      cartSections: Array.isArray(estimationDetails?.cartSections) ? estimationDetails.cartSections : [],
      layout: Array.isArray(estimationDetails?.layout) ? estimationDetails.layout : [],
    });
  }, [estimationDetails?.cartSections, estimationDetails?.layout, estimationDetails?.products]);
  const documentState = useMemo(
    () =>
      createGeneratedSalesOrderDocumentState({
        id,
        documentType,
        estimationDetails,
        renderedLayoutRows,
        explicitSnapshotId,
        expectedSnapshotRevision,
        requestCompleted: renderState.requestCompleted,
        error: renderState.error,
      }),
    [
      documentType,
      estimationDetails,
      expectedSnapshotRevision,
      explicitSnapshotId,
      id,
      renderState.error,
      renderState.requestCompleted,
      renderedLayoutRows,
    ]
  );
  useEffect(() => {
    publishGeneratedSalesOrderDocumentState(documentState);
  }, [documentState]);
  useEffect(() => {
    if (!isRenderMode || !documentState.hasDocumentData) return;
    const logKey = [
      documentState.documentId || '',
      documentState.productCount || 0,
      documentState.layoutRowCount || 0,
      documentState.renderedSnapshotId || '',
      documentState.renderedSnapshotRevision || '',
    ].join(':');
    if (hydratedLogKeyRef.current === logKey) return;
    hydratedLogKeyRef.current = logKey;
    console.log('[generated-sales-order-render] document:hydrated', {
      estimationId: documentState.documentId,
      productCount: documentState.productCount,
    });
  }, [
    documentState.documentId,
    documentState.hasDocumentData,
    documentState.layoutRowCount,
    documentState.productCount,
    documentState.renderedSnapshotId,
    documentState.renderedSnapshotRevision,
    isRenderMode,
  ]);
  useEffect(() => {
    if (!isRenderMode) return;
    if (!documentState.ready) {
      readyLoggedRef.current = false;
      return;
    }
    if (readyLoggedRef.current) return;
    readyLoggedRef.current = true;
    console.log('[generated-sales-order-render] document:ready');
  }, [documentState.ready, isRenderMode]);
  // ─────────────────────────────────────────────────────────────
  const viewKey = isEstimate ? 'estimate' : 'salesOrder';
  const { headerConfig, footerAddress, termsHtml } = useProjectTemplateConfig({
    instanceUniqueId,
    viewKey,
  });

  const handleComments = async (estimationId, productData, comment) => {
    if (!canShowComments) {
      toast('Comments are disabled for this estimate.');
      return;
    }

    const productId = resolveGeneratedEstimateProductId(productData);
    const productIdentityKey = resolveGeneratedEstimateProductIdentityKey(productData);
    if (!productId || !productIdentityKey) {
      toast.error('Unable to identify this estimate product.', { id: 20 });
      return;
    }
    if (commentPendingRef.current[productIdentityKey] || commentStateByProduct[productIdentityKey]?.pending) {
      return;
    }
    commentPendingRef.current[productIdentityKey] = true;

    const previousComment = String(
      commentStateByProduct[productIdentityKey]?.comment ??
      findGeneratedEstimateProductById(estimationDetails?.products || [], productIdentityKey)?.comment ??
      ''
    );

    try {
      setCommentStateByProduct((prev) => ({
        ...prev,
        [productIdentityKey]: {
          pending: true,
          comment,
          previousComment,
        },
      }));

      setEstimationDetails((prev) => {
        if (!prev || !Array.isArray(prev.products)) return prev;
        return {
          ...prev,
          products: updateGeneratedEstimateProductComment(prev.products, productIdentityKey, comment),
        };
      });

      const payload = {
        estimationId,
        productId,
        orderItemId: resolveGeneratedEstimateOrderItemId(productData),
        lineId: resolveGeneratedEstimateLineId(productData),
        comment,
      };
      await updateComment(payload);
      confirmedCommentRef.current[productIdentityKey] = { comment };
      setCommentStateByProduct((prev) => ({
        ...prev,
        [productIdentityKey]: {
          pending: false,
          comment,
          previousComment,
        },
      }));
      await getEstimation({ silent: true });
      toast.success('Your comment has been posted successfully.', { id: 19 });
    } catch (error) {
      delete confirmedCommentRef.current[productIdentityKey];
      setCommentStateByProduct((prev) => {
        const next = { ...prev };
        delete next[productIdentityKey];
        return next;
      });
      setEstimationDetails((prev) => {
        if (!prev || !Array.isArray(prev.products)) return prev;
        return {
          ...prev,
          products: updateGeneratedEstimateProductComment(prev.products, productIdentityKey, previousComment),
        };
      });
      toast.error('Failed to post comment', { id: 20 });
    } finally {
      delete commentPendingRef.current[productIdentityKey];
    }
  };

  // ─────────────────────────────────────────────────────────────
  // Actions from the public footer (Approve / Request Changes)
  // Submit exactly what we intend; don't inject approval defaults.
  const submitPortalAction = async (patch = {}, successMsg = 'Saved') => {
    try {
      if (!estimationDetails?.estimationId) throw new Error('Missing estimationId');
      setLoading(true);
      const payload = { orderId: estimationDetails?.orderId, ...patch };
      const hasToken = hasUsableAccessToken();
      const isApproveIntent =
        payload.customerAgreed === true ||
        payload.customerAgreed === 'true' ||
        payload.approveAction === true;
      const isRequestChangeIntent =
        payload.requestChange === true ||
        payload.requestChange === 'true';

      if (!hasToken && (isApproveIntent || isRequestChangeIntent)) {
        await submitPublicSalesOrderAction(estimationDetails.estimationId, {
          action: isApproveIntent ? 'approve' : 'request_change',
          orderId: estimationDetails?.orderId,
          agreedByName: payload.agreedByName,
          agreedByEmail: payload.agreedByEmail,
          requestChangeComment: payload.requestChangeComment,
        });
      } else {
        await updateEstimation(estimationDetails.estimationId, payload);
      }
      await getEstimation();
      toast.success(successMsg, { id: `so-action-${Date.now()}` });
      return { ok: true };
    } catch (error) {
      console.error('submitPortalAction error:', error);
      const status = Number(error?.response?.status || 0);
      const gate = error?.response?.data?.gate || null;
      const message = error?.response?.data?.message || '';

      if (status === 409 && gate) {
        setProofBundle((prev) => {
          if (!prev) {
            return {
              rows: [],
              summary: gate,
            };
          }
          return {
            ...prev,
            summary: {
              ...(prev.summary || {}),
              ...gate,
            },
          };
        });
        if (message) {
          toast.error(message, { id: `so-action-gate-${Date.now()}` });
        }
        return {
          ok: false,
          blockedByProofGate: true,
          gate,
          message,
        };
      }

      if (status === 402) {
        toast.error(message || 'Payment is required before approval can continue.', {
          id: `so-action-payment-${Date.now()}`,
        });
        return {
          ok: false,
          paymentRequired: true,
          error,
          message,
        };
      }

      toast.error('Failed to update the sales order', { id: `so-action-err-${Date.now()}` });
      return {
        ok: false,
        error,
      };
    } finally {
      setLoading(false);
    }
  };

  // APPROVE
  const approveSalesOrder = async ({ agreedByName = '', agreedByEmail = '' } = {}) =>
    submitPortalAction(
      {
        status: 2,              // backend will move to 3 on agree branch
        customerAgreed: true,   // boolean is fine; backend normalizes
        agreedByName,
        agreedByEmail,
      },
      'Thank you for approving the sales order. We’ll get things moving!'
    );

  // REQUEST CHANGES
  const requestSalesOrderChanges = async ({ comment = '', agreedByName = '', agreedByEmail = '' } = {}) =>
    submitPortalAction(
      {
        status: 4,                 // explicitly mark "Changes Requested"
        requestChange: true,       // ensures backend takes the reject branch first
        requestChangeComment: comment,
        customerAgreed: false,     // be explicit to avoid any ambiguity
        agreedByName,
        agreedByEmail,
      },
      'Your change request was sent. We’ll follow up shortly.'
    );
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (id) {
      getEstimation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search]);

  useEffect(() => {
    const orderId = estimationDetails?.orderId || estimationDetails?.OrderId;
    if (!orderId) return;
    let alive = true;
    const loadInstance = async () => {
      try {
        const url = `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-order/${orderId}`;
        const res = await axios.get(url);
        const order = res?.data?.order || {};
        const raw = order?.InstanceUniqueIds;
        let resolved = null;
        if (Array.isArray(raw)) {
          resolved = raw[0] || null;
        } else if (typeof raw === 'string' && raw.trim()) {
          try {
            const parsed = JSON.parse(raw);
            resolved = Array.isArray(parsed) ? parsed[0] : parsed;
          } catch {
            resolved = raw;
          }
        }
        if (alive) setInstanceUniqueId(resolved);
      } catch (err) {
        if (alive) setInstanceUniqueId(null);
      }
    };
    loadInstance();
    return () => {
      alive = false;
    };
  }, [estimationDetails?.orderId, estimationDetails?.OrderId]);

  useEffect(() => {
    const orderId = estimationDetails?.orderId || estimationDetails?.OrderId || estimationDetails?.orderID;
    setGeneratedHistoryAllowed(false);
    setSnapshotHistoryOpen(false);
    if (!orderId || isRenderMode) return;
    let active = true;
    axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/${orderId}/generated-history-visibility`, {
      headers: { accessToken: localStorage.getItem("accessToken") || "" },
    }).then(({ data }) => {
      if (active) setGeneratedHistoryAllowed(data?.allowed === true);
    }).catch(() => {
      if (active) setGeneratedHistoryAllowed(false);
    });
    return () => { active = false; };
  }, [estimationDetails?.orderId, estimationDetails?.OrderId, estimationDetails?.orderID, isRenderMode]);

  const reloadProofBundle = useCallback(async () => {
    const documentType = isEstimatePage ? 'estimate' : 'sales-order';
    const documentId = normalizeMaybeId(id || estimationDetails?.estimationId);
    const orderId = normalizeMaybeId(
      estimationDetails?.orderId || estimationDetails?.OrderId || estimationDetails?.orderID
    );

    if (!documentType || !documentId) {
      setProofBundle(null);
      setProofBundleError('');
      return;
    }

    try {
      setProofBundleLoading(true);
      setProofBundleError('');
      const response = await fetchDocumentArtworkProofs(documentType, documentId, {
        ...(orderId ? { orderId } : {}),
      });
      setProofBundle(response || null);
    } catch (error) {
      console.warn('[GeneratedSalesOrder] artwork proof bundle load failed:', error);
      setProofBundle(null);
      setProofBundleError('Artwork proofs could not be loaded right now. The document can still be reviewed.');
    } finally {
      setProofBundleLoading(false);
    }
  }, [estimationDetails?.OrderId, estimationDetails?.estimationId, estimationDetails?.orderId, estimationDetails?.orderID, id, isEstimatePage]);

  useEffect(() => {
    reloadProofBundle();
  }, [reloadProofBundle]);

  useEffect(() => {
    if (isRenderMode) {
      setCartOrder(null);
      return undefined;
    }
    let mounted = true;
    const loadOrderForCart = async () => {
      const orderId = estimationDetails?.orderId || estimationDetails?.OrderId;
      if (!orderId) return;
      try {
        const res = await axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-order/${orderId}`);
        if (mounted) setCartOrder(res?.data?.order || null);
      } catch (err) {
        if (mounted) setCartOrder(null);
      }
    };

    loadOrderForCart();
    return () => {
      mounted = false;
    };
  }, [estimationDetails?.orderId, estimationDetails?.OrderId, isRenderMode]);

  const syncCartProducts = async (nextProducts) => {
    const orderId = estimationDetails?.orderId || estimationDetails?.OrderId;
    if (!orderId || !cartOrder) return;

    await updateProductsToOrder(orderId, {
      customer_name: cartOrder.CustomerName,
      email: cartOrder.Email,
      phone: cartOrder.Phone,
      total: cartOrder.Total,
      items: nextProducts,
      siteURL: cartOrder.SiteURL,
      order_status: cartOrder.OrderStatus,
      assigned_to: cartOrder.AssignedTo,
      AttachedCustomerId: cartOrder?.attachedCustomers?.[0] || '',
    });

    const refreshed = await axios.get(`${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/get-order/${orderId}`);
    setCartOrder(refreshed?.data?.order || null);
  };

  const handleDeleteCartProduct = async (index) => {
    const current = Array.isArray(cartOrder?.Products) ? [...cartOrder.Products] : [];
    if (!current[index]) return;
    current.splice(index, 1);
    await syncCartProducts(current);
    await getEstimation();
  };

  const handleCopyCartProduct = async (index) => {
    const current = Array.isArray(cartOrder?.Products) ? [...cartOrder.Products] : [];
    if (!current[index]) return;
    current[index] = { ...current[index], Quantity: Number(current[index].Quantity || 1) + 1 };
    await syncCartProducts(current);
    await getEstimation();
  };

  const handleRegenerateFromDock = async (approvalMode = null) => {
    const orderId = estimationDetails?.orderId || estimationDetails?.OrderId;
    const estimationId = estimationDetails?.estimationId;
    if (!orderId || !estimationId || regeneratingDocument) return;
    const isApprovedSalesOrder = !isEstimateStatus(estimationDetails?.status) && Number(getBaseStatus(estimationDetails?.status)) === 3;
    if (isApprovedSalesOrder && !approvalMode) {
      setApprovalRegeneration({ open: true, mode: "reset" });
      return;
    }

    const requiresUpfrontPayment =
      estimationDetails?.requireUpfrontPayment === true ||
      estimationDetails?.requireUpfrontPayment === 1 ||
      estimationDetails?.requireUpfrontPayment === '1' ||
      estimationDetails?.requireUpfrontPayment === 'true';
    const hasAttachedInvoice = Boolean(
      estimationDetails?.depositInvoiceId ||
      estimationDetails?.deposit_invoice_id ||
      estimationDetails?.invoiceId ||
      estimationDetails?.invoice_id ||
      estimationDetails?.invoiceNumber ||
      estimationDetails?.depositAmountSnapshot ||
      estimationDetails?.deposit_amount_snapshot
    );
    let hasPaidInvoiceActivity = false;
    if (!isApprovedSalesOrder && requiresUpfrontPayment) {
      try {
        const invoices = await fetchInvoicesListByOrder(orderId);
        hasPaidInvoiceActivity = Array.isArray(invoices) && invoices.some((invoice) => isInvoicePaidStatus(invoice));
      } catch (error) {
        console.warn('[GeneratedSalesOrder] unable to inspect invoice payment state before regeneration:', error);
      }
    }
    const shouldRefreshUpfrontInvoice = requiresUpfrontPayment && !hasPaidInvoiceActivity && !isApprovedSalesOrder;

    if (!isApprovedSalesOrder && requiresUpfrontPayment) {
      const shouldProceed = window.confirm(
        hasPaidInvoiceActivity
          ? 'This Sales Order already has a paid invoice. Regenerating will update the Sales Order and related purchasing/billing details, but existing paid invoices will remain unchanged. If the revised total is higher, additional charges or an uninvoiced balance may be displayed later. Continue?'
          : 'This document requires upfront payment. Regenerating will recalculate and refresh the deposit/invoice pricing based on the revised total. Continue?'
      );
      if (!shouldProceed) return;
    }

    if (!isApprovedSalesOrder && hasAttachedInvoice && !hasPaidInvoiceActivity) {
      const shouldProceed = window.confirm(
        'Please note: an attached invoice/deposit will be revised when this document is regenerated. Click OK to continue or Cancel to stop.'
      );
      if (!shouldProceed) return;
    }

    const regenerateOptions = {
      ...(!isEstimateStatus(estimationDetails?.status) ? { source: 'order-cart' } : {}),
      recalculateUpfrontPayment: shouldRefreshUpfrontInvoice,
      reviseAttachedInvoice: (!isApprovedSalesOrder && hasAttachedInvoice && !hasPaidInvoiceActivity) || shouldRefreshUpfrontInvoice,
      recreateDraftDepositInvoice: shouldRefreshUpfrontInvoice,
      recreateDepositInvoice: shouldRefreshUpfrontInvoice,
      regenerateDepositInvoice: shouldRefreshUpfrontInvoice,
      forceRecreateDepositInvoice: shouldRefreshUpfrontInvoice,
      currency: estimationDetails?.currency || undefined,
      ...(approvalMode ? { approvalMode } : {}),
    };

    try {
      regenerationInFlightRef.current = true;
      setRegeneratingDocument(true);
      let response;

      if (isEstimateStatus(estimationDetails?.status)) {
        response = await regenerateEstimateFromCart(orderId, estimationId, regenerateOptions);
      } else {
        response = await regenerateSalesOrderFromCart(orderId, estimationId, regenerateOptions);
      }

      if (shouldRefreshUpfrontInvoice) {
        await createInvoice(orderId, {
          instanceUniqueId,
          role: 'deposit',
          sendEmailNow: false,
        });
      }

      const nextDocumentId =
        response?.document?.estimationId ||
        response?.salesOrder?.estimationId ||
        estimationId;
      const nextDocumentType = isEstimateStatus(estimationDetails?.status) ? 'estimate' : 'sales-order';
      const nextPath = `${nextDocumentType === 'estimate' ? '/generated-estimate' : '/generated-sales-order'}/${nextDocumentId}`;

      history.replace(nextPath);
      const refreshed = await fetchEstimationDetails(nextDocumentId, {
        documentType: nextDocumentType,
      });
      hydrateGeneratedDocument(refreshed?.salesOrder || {});
      if (response?.message) {
        toast.success(response.message, { duration: 1800 });
      }
      return true;
    } catch (error) {
      const routingError = getDecorationRoutingError(error);
      if (routingError) {
        console.error('Sales Order decoration routing validation failed', {
          code: error?.response?.data?.code,
          issues: routingError.issues.map(({ orderItemId, decorationId, reason }) => ({ orderItemId, decorationId, reason })),
        });
        setDecorationRoutingError({ open: true, issues: routingError.issues });
        return false;
      }
      toast.error(
        error?.response?.data?.message ||
        'Unable to regenerate this document right now.'
      );
      return false;
    } finally {
      regenerationInFlightRef.current = false;
      setRegeneratingDocument(false);
    }
  };

  const handleResetSalesOrderApproval = async () => {
    const orderId = estimationDetails?.orderId || estimationDetails?.OrderId;
    const estimationId = estimationDetails?.estimationId;
    if (!orderId || !estimationId || resettingApproval) return false;

    try {
      setResettingApproval(true);
      const response = await resetSalesOrderApproval(orderId, estimationId, {});
      const refreshed = await fetchEstimationDetails(estimationId, {
        documentType: 'sales-order',
      });
      hydrateGeneratedDocument(refreshed?.salesOrder || {});
      setResetApprovalDialogOpen(false);
      toast.success(
        response?.message || 'Sales Order approval was reset. Client approval is required again.'
      );
      return true;
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
        'Unable to reset Sales Order approval right now.'
      );
      return false;
    } finally {
      setResettingApproval(false);
    }
  };

  const loadSnapshotHistory = async () => {
    const orderId = estimationDetails?.orderId || estimationDetails?.OrderId || estimationDetails?.orderID;
    const estimationId = estimationDetails?.estimationId || id;

    if (!orderId || !estimationId) {
      setSnapshotHistoryError('Missing order or document ID.');
      return;
    }

    const isSalesOrderPage = location.pathname.startsWith('/generated-sales-order');
    const normalizeDocType = (snapshot) =>
      String(snapshot?.documentType || snapshot?.type || snapshot?.status || snapshot?.snapshotType || '').toLowerCase();
    const isEstimateSnapshot = (snapshot) => {
      const type = normalizeDocType(snapshot);
      return (type.includes('estimate') || type.includes('estimation')) && !type.includes('presentation');
    };
    const isSalesOrderSnapshot = (snapshot) => {
      const type = normalizeDocType(snapshot);
      return (type.includes('sales-order') || type.includes('sales order') || type.includes('sales_order')) && !type.includes('presentation');
    };
    const getSnapshotDocumentId = (snapshot) =>
      snapshot?.estimationId || snapshot?.documentId || snapshot?.id || snapshot?.estimationID;

    try {
      setSnapshotHistoryLoading(true);
      setSnapshotHistoryError('');
      let res;
      try {
        res = await fetchCartSnapshots(orderId);
      } catch (authErr) {
        try {
          const fallbackUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/${orderId}/cart-snapshots`;
          const fallbackRes = await axios.get(fallbackUrl);
          res = fallbackRes?.data;
        } catch (publicErr) {
          const documentType = isEstimatePage ? 'estimate' : 'sales-order';
          const documentSnapshotUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/orders/${orderId}/document-snapshots/${documentType}/${id}`;
          const documentSnapshotRes = await axios.get(documentSnapshotUrl);
          res = documentSnapshotRes?.data;
        }
      }
      const rows = Array.isArray(res?.snapshots)
        ? res.snapshots
        : Array.isArray(res?.cartSnapshots)
          ? res.cartSnapshots
          : Array.isArray(res)
            ? res
            : [];
      const filtered = rows
        .filter((row) => {
          if (isEstimatePage) return isEstimateSnapshot(row);
          if (isSalesOrderPage) return isSalesOrderSnapshot(row);
          return false;
        })
        .filter((row) => Boolean(getSnapshotDocumentId(row)));
      setSnapshotHistory(filtered);
    } catch (err) {
      console.error('[snapshot history] failed:', err);
      setSnapshotHistoryError('Failed to load document history.');
    } finally {
      setSnapshotHistoryLoading(false);
    }
  };

  const openSnapshotHistory = async () => {
    if (!generatedHistoryAllowed) return;
    setSnapshotHistoryOpen(true);
    await loadSnapshotHistory();
  };
  const historyTitle = isEstimatePage ? 'Estimate history' : 'Sales order history';
  const historyFabLabel = isEstimatePage ? 'Estimate history' : 'Sales order history';
  const snapshotRevisionFromUrl = new URLSearchParams(location.search).get('snapshotRevision');
  const normalizedCurrentRevision = snapshotRevisionFromUrl ? Number(snapshotRevisionFromUrl) : null;

  const totalsCents = useMemo(
      () => (estimationDetails ? computeTotalsFromSO(estimationDetails) : null),
       [estimationDetails]
     );
  const grandTotal = totalsCents ? fromCents(totalsCents.totalCents) : 0;

  const renderedProducts = useMemo(() => {
    // Always attach mapped proof files back to their artwork target so the
    // customer can see the selected proof beside the matching decoration.
    // Complete-package mappings are rendered read-only at the artwork row;
    // the package-level decision still happens in the guided proof step.
    const productsWithProofs = attachProofReplacementsToProducts(
      estimationDetails?.products || [],
      proofBundle?.rows || []
    );

    return productsWithProofs.map((product) => {
      const productIdentityKey = resolveGeneratedEstimateProductIdentityKey(product);
      const localCommentState = productIdentityKey ? commentStateByProduct[productIdentityKey] : null;
      return localCommentState ? { ...product, comment: localCommentState.comment } : product;
    });
  }, [commentStateByProduct, estimationDetails?.products, proofBundle?.rows]);

  const renderedEstimationDetails = useMemo(
    () => (estimationDetails ? { ...estimationDetails, products: renderedProducts } : null),
    [estimationDetails, renderedProducts]
  );
  const renderedDisplayLayoutRows = useMemo(() => {
    return resolveCustomerDocumentLayoutRows({
      products: Array.isArray(renderedEstimationDetails?.products)
        ? renderedEstimationDetails.products
        : [],
      cartSections: Array.isArray(estimationDetails?.cartSections)
        ? estimationDetails.cartSections
        : [],
      layout: Array.isArray(estimationDetails?.layout)
        ? estimationDetails.layout
        : [],
    });
  }, [
    estimationDetails?.cartSections,
    estimationDetails?.layout,
    renderedEstimationDetails?.products,
  ]);
  const baseStatus = String(estimationDetails?.status || '').split('_')[0];
  const isAgreementStage = !isEstimatePage && baseStatus === '2';
  const isOrderFirstApprovalFlow = estimationDetails?.clientApprovalFlow === 'order_first';
  const customerAgreedValue = estimationDetails?.customerAgreed;
  const hasCustomerApprovedSalesOrder =
    customerAgreedValue === true ||
    customerAgreedValue === 1 ||
    customerAgreedValue === '1' ||
    customerAgreedValue === 'true';
  const isApprovedSalesOrderStatus = ['3', '6', '7', '8', '9', '10'].includes(baseStatus);
  const isSalesOrderStepComplete = hasCustomerApprovedSalesOrder && isApprovedSalesOrderStatus;
  const canShowCompleteApprovalPackage =
    !isOrderFirstApprovalFlow || isSalesOrderStepComplete;
  const derivedArtworkProofGate = useMemo(() => {
    const summaryFromBundle =
      proofBundle?.summary && typeof proofBundle.summary === 'object'
        ? proofBundle.summary
        : getProofApprovalSummaryFromRows(proofBundle?.rows || []);
    const total = Number(summaryFromBundle?.total || 0);
    return {
      total,
      approved: Number(summaryFromBundle?.approved || 0),
      rejected: Number(summaryFromBundle?.rejected || 0),
      pending: Number(summaryFromBundle?.pending || 0),
      canProceed: summaryFromBundle?.canProceed !== false,
      canSign: summaryFromBundle?.canSign !== false,
      blockingReason: summaryFromBundle?.blockingReason || null,
      firstBlockingMappingId: summaryFromBundle?.firstBlockingMappingId ?? null,
      firstBlockingProofId: summaryFromBundle?.firstBlockingProofId ?? null,
      isActive: isAgreementStage && total > 0,
    };
  }, [proofBundle?.rows, proofBundle?.summary, isAgreementStage]);
  const hasArtworkProofingAttached = useMemo(
    () => Number(derivedArtworkProofGate?.total || 0) > 0,
    [derivedArtworkProofGate?.total]
  );
  const completeApprovalPackage = useMemo(() => {
    const serverPackage =
      Array.isArray(proofBundle?.packages) ? proofBundle.packages[0] || null : null;
    if (serverPackage) return serverPackage;

    // Defensive fallback for older/stale bundle payloads: if the mappings
    // themselves are complete-package mappings, build the package view from
    // those rows rather than dropping the customer's proof UI entirely.
    const completeRows = (Array.isArray(proofBundle?.rows) ? proofBundle.rows : [])
      .filter(
        (row) =>
          String(row?.mapping?.approvalMode || '').trim().toLowerCase() ===
            'complete_package' &&
          String(row?.mapping?.approvalGroupId || '').trim()
      );
    if (!completeRows.length) return null;

    const approvalGroupId = String(
      completeRows[0]?.mapping?.approvalGroupId || ''
    ).trim();
    const rows = completeRows.filter(
      (row) =>
        String(row?.mapping?.approvalGroupId || '').trim() === approvalGroupId
    );
    if (!approvalGroupId || !rows.length) return null;

    return {
      approvalGroupId,
      approvalMode: 'complete_package',
      sourceType: rows[0]?.mapping?.sourceType || null,
      sourceProvider: rows[0]?.mapping?.sourceProvider || null,
      rows,
      fileCount: rows.length,
      summary: getProofApprovalSummaryFromRows(rows),
    };
  }, [proofBundle?.packages, proofBundle?.rows]);

  const scrollToArtworkProof = useCallback((mappingId = null) => {
    if (typeof document === 'undefined') return;
    const targetId =
      mappingId != null
        ? `artwork-proof-mapping-${mappingId}`
        : derivedArtworkProofGate?.firstBlockingMappingId != null
          ? `artwork-proof-mapping-${derivedArtworkProofGate.firstBlockingMappingId}`
          : '';

    let target = targetId ? document.getElementById(targetId) : null;
    if (!target && completeApprovalPackage?.approvalGroupId) {
      target = document.getElementById(`artwork-proof-package-${completeApprovalPackage.approvalGroupId}`);
    }
    if (!target) {
      target =
        document.querySelector('[data-artwork-proof-rejected="true"]') ||
        document.querySelector('[data-artwork-proof-blocking="true"]');
    }
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [completeApprovalPackage?.approvalGroupId, derivedArtworkProofGate?.firstBlockingMappingId]);

  useEffect(() => {
    if (isRenderMode) return;
    if (!derivedArtworkProofGate?.isActive || derivedArtworkProofGate?.canProceed !== false) return;
    if (proofBundleLoading) return;
    scrollToArtworkProof(derivedArtworkProofGate?.firstBlockingMappingId ?? null);
  }, [
    derivedArtworkProofGate?.isActive,
    derivedArtworkProofGate?.canProceed,
    derivedArtworkProofGate?.firstBlockingMappingId,
    isRenderMode,
    proofBundleLoading,
    scrollToArtworkProof,
  ]);
    

  const otherServices =
    getSalesOrderOtherServices(estimationDetails)
      .filter((service) => isCustomerFacingVisibleLine(service))
      .map((service) => {
        const amounts = resolveOtherServiceCustomerAmounts(service);

        return {
          item: service.Title ?? service.title,
          description: getServiceDescription(service),
          quantity: amounts.quantity,
          subtotal: amounts.subtotal,
          tax: amounts.tax,
          total: amounts.total,
          taxRate: amounts.taxRate,
        };
      });

  // ─────────────────────────────────────────────────────────────
  // Edit button gating (requires accessToken AND role != 'customer')
  const decodeBase64Url = (str) => {
    try {
      // base64url -> base64
      const pad = '='.repeat((4 - (str.length % 4)) % 4);
      const base64 = (str + pad).replace(/-/g, '+').replace(/_/g, '/');
      const json = atob(base64);
      // handle utf-8
      const text = decodeURIComponent(
        json
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(text);
    } catch {
      try {
        return JSON.parse(atob(str));
      } catch {
        return null;
      }
    }
  };

  const getRoleFromAccessToken = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = decodeBase64Url(parts[1]);
    return payload?.role ?? payload?.user?.role ?? null;
  };

  const getAccessTokenPayload = () => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    return decodeBase64Url(parts[1]);
  };

  const hasAccessToken = Boolean(localStorage.getItem('accessToken'));
  const accessTokenPayload = useMemo(() => getAccessTokenPayload() || {}, [hasAccessToken]);
  const role = String(getRoleFromAccessToken() || localStorage.getItem('userRole') || '').toLowerCase();
  const normalizedRole = normalizeRole(role);
  const currentUser = useMemo(() => ({
    id:
      accessTokenPayload?.id ||
      accessTokenPayload?.user?.id ||
      localStorage.getItem('userId') ||
      null,
    role: normalizedRole,
  }), [accessTokenPayload, normalizedRole]);
  const canEdit = hasAccessToken && role !== 'customer';
  const hasHistoryDock = Boolean(estimationDetails && (estimationDetails?.orderId || estimationDetails?.OrderId || estimationDetails?.orderID));
  const documentRequiresRegeneration = estimationDetails?.documentRequiresRegeneration === true;
  const canRegenerateGeneratedDocument = canInternalUserRegenerateGeneratedDocument({
    currentUser,
    salesOrder: estimationDetails,
  });
  const canResetApproval = canInternalUserResetSalesOrderApproval({
    currentUser,
    salesOrder: estimationDetails,
  });
  const normalizeBooleanSetting = (value, fallback = false) => {
    if (value === true || value === 1 || value === '1' || value === 'true') return true;
    if (value === false || value === 0 || value === '0' || value === 'false') return false;
    return fallback;
  };
  const guidedHasPostApprovalStep =
    isOrderFirstApprovalFlow &&
    (
      hasArtworkProofingAttached ||
      (
        normalizeBooleanSetting(estimationDetails?.requireUpfrontPayment, false) &&
        normalizeBooleanSetting(estimationDetails?.paymentDueOnSign, true)
      )
    );
  const proofDecisionContext = useMemo(
    () => ({
      hasAccessToken,
      isInternal: hasAccessToken && role !== 'customer',
      internalEmail:
        accessTokenPayload?.email ||
        accessTokenPayload?.user?.email ||
        localStorage.getItem('userEmail') ||
        '',
      internalName:
        accessTokenPayload?.first_name ||
        accessTokenPayload?.firstName ||
        accessTokenPayload?.name ||
        accessTokenPayload?.user?.first_name ||
        accessTokenPayload?.user?.firstName ||
        accessTokenPayload?.user?.name ||
        localStorage.getItem('userName') ||
        '',
      priorClientApprovalEmail:
        estimationDetails?.agreedByEmail ||
        estimationDetails?.approvedByEmail ||
        estimationDetails?.clientEmail ||
        estimationDetails?.Email ||
        '',
    }),
    [
      hasAccessToken,
      role,
      accessTokenPayload,
      estimationDetails?.agreedByEmail,
      estimationDetails?.approvedByEmail,
      estimationDetails?.clientEmail,
      estimationDetails?.Email,
    ]
  );

  const handleProofDecisionUpdated = useCallback(({ mappingId, proof, mapping, response }) => {
    if (!mappingId || !proof) return;
    const decisionState = String(response?.proof?.approvals?.client?.state || response?.proof?.status || proof?.approvals?.client?.state || proof?.status || "").toLowerCase();
    let nextMappingIdToScroll = null;
    setProofBundle((prev) => {
      if (!prev || !Array.isArray(prev.rows)) return prev;
      const nextRows = prev.rows.map((row) =>
        Number(row?.mapping?.id) === Number(mappingId)
          ? {
              ...row,
              proof,
              mapping: mapping || row.mapping,
            }
          : row
      );
      if (decisionState === 'approved') {
        nextMappingIdToScroll = findNextActionableProofMappingId(nextRows, mappingId);
      }
      return {
        ...prev,
        rows: nextRows,
        summary: getProofApprovalSummaryFromRows(nextRows),
      };
    });
    if (response?.salesOrder && typeof response.salesOrder === 'object') {
      setEstimationDetails((prev) =>
        prev
          ? {
              ...prev,
              status: response.salesOrder?.status ?? prev.status,
              customerAgreed: response.salesOrder?.customerAgreed ?? prev.customerAgreed,
              requestChangeComment:
                response.salesOrder?.requestChangeComment ?? prev.requestChangeComment,
              requestChangeAt: response.salesOrder?.requestChangeAt ?? prev.requestChangeAt,
            }
          : prev
      );
    }
    if (response?.salesOrderAgreementSync?.approved === true) {
      toast.success('All mapped proofs approved. Sales order agreement recorded.');
    }
    if (decisionState === 'approved' && nextMappingIdToScroll) {
      setTimeout(() => {
        document
          .getElementById(`artwork-proof-mapping-${nextMappingIdToScroll}`)
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 0);
    }
  }, []);

  const handlePackageDecisionCompleted = useCallback(async (response) => {
    const agreementApproved = response?.salesOrderAgreementSync?.approved === true;
    const explicitCustomerAgreedValue = response?.salesOrder?.customerAgreed;
    const shouldApplyCustomerAgreed =
      agreementApproved ||
      explicitCustomerAgreedValue === false ||
      explicitCustomerAgreedValue === 0 ||
      explicitCustomerAgreedValue === '0' ||
      explicitCustomerAgreedValue === 'false';

    if (response?.salesOrder && typeof response.salesOrder === 'object') {
      setEstimationDetails((prev) =>
        prev
          ? {
              ...prev,
              status: response.salesOrder?.status ?? prev.status,
              customerAgreed: shouldApplyCustomerAgreed
                ? response.salesOrder?.customerAgreed ?? prev.customerAgreed
                : prev.customerAgreed,
              requestChangeComment:
                response.salesOrder?.requestChangeComment ?? prev.requestChangeComment,
              requestChangeAt: response.salesOrder?.requestChangeAt ?? prev.requestChangeAt,
              agreedByName: agreementApproved
                ? response.salesOrderAgreementSync?.agreedBy?.name ??
                  response.salesOrder?.agreedByName ??
                  prev.agreedByName
                : prev.agreedByName,
              agreedByEmail: agreementApproved
                ? response.salesOrderAgreementSync?.agreedBy?.email ??
                  response.salesOrder?.agreedByEmail ??
                  prev.agreedByEmail
                : prev.agreedByEmail,
            }
          : prev
      );
    }
    if (agreementApproved) {
      toast.success('All artwork proofs approved. Sales order agreement recorded.');
    }
    await reloadProofBundle();
  }, [reloadProofBundle]);

  const formatMoney = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    return `$${num.toFixed(2)}`;
  };

  const getProductDescription = (product) =>
    product?.Description || product?.description || product?.details || product?.ProductDescription || '';

  const buildEditUrl = () => {
    const orderId = estimationDetails?.orderId;
    const estimationId = estimationDetails?.estimationId;
    if (!orderId || !estimationId) return '#';
    const m = location.pathname.match(/\/instance\/([^/]+)/i);
    const instanceSlug = m?.[1] || 'elite-promo-marketing';
    const tabType = isEstimateStatus(estimationDetails?.status) ? 'estimation' : 'sales-order';
    return `/instance/${instanceSlug}/sales/orders/${orderId}/${tabType}/${estimationId}`;
  };
  const isGeneratedSalesOrderRenderReady = useMemo(() => {
    return documentState.ready;
  }, [documentState.ready]);
  // ─────────────────────────────────────────────────────────────

  return (
    <>
      <Toaster position="top-right" containerClassName={styles.printHidden} />
      <GeneratedDocumentPrintActions
        isRenderMode={isRenderMode}
        isLoggedIn={hasUsableAccessToken()}
        ready={documentState.ready && !regeneratingDocument}
        bottomLeftOffset={canEdit ? (hasHistoryDock ? 184 : 128) : (hasHistoryDock ? 72 : 16)}
      />
      <Box className={styles.rootContainer}>
        <Container className={styles.printDocument} maxWidth="lg">
          {isGeneratedSalesOrderRenderReady ? (
            <div
              id="generated-sales-order-ready"
              data-ready="true"
              style={{ display: 'none' }}
            />
          ) : null}
          {regeneratingDocument ? (
            <Box className={styles.regenerationViewport}>
              <RegenerationLoader
                type={estimationDetails?.regenerateDocumentType || (isEstimatePage ? 'estimate' : 'sales-order')}
              />
            </Box>
          ) : (
            <>
          {loading && <EstimationLoading />}

          <Header
            presentationDate={estimationDetails?.inhandsDate}
            id={estimationDetails?.estimationId}
            Introduction={estimationDetails?.introduction}
            status={estimationDetails?.status}
            headerConfig={headerConfig}
            isLoaded={!!estimationDetails}
            isEstimateView={isEstimatePage}
          />

          <EstimationDetails
            estimationDetails={estimationDetails}
            isEstimateView={isEstimatePage}
            showScreen={Boolean(estimationDetails) && (isEstimatePage || isAfterEstimateStatus(estimationDetails?.status))}
          />

          <Box className={styles.PresentationContainer}>
            {!isRenderMode && proofBundleLoading ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                Loading artwork proof mappings…
              </Alert>
            ) : null}
            {!isRenderMode && proofBundleError ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {proofBundleError}
              </Alert>
            ) : null}
            {documentRequiresRegeneration ? (
              <RegenerationRequired
                canRegenerate={canRegenerateGeneratedDocument}
                count={estimationDetails?.pendingCartProductCount || 0}
                documentType={estimationDetails?.regenerateDocumentType || (isEstimatePage ? 'estimate' : 'sales-order')}
                isLoading={regeneratingDocument}
                onRegenerate={handleRegenerateFromDock}
              />
            ) : (
              <Grid container spacing={2} className={styles.presentationCard}>
                {renderedDisplayLayoutRows.map((row, index) => (
                  <Grid item xs={12} key={row.key || index} className={`${styles.presentationGrid} ${row.type === "section" ? styles.sectionTitleGrid : ""}`}>
                    {row.type === "section" ? (
                      <DocumentSectionTitle title={row.title} style={row.style} />
                    ) : (
                      <ProductDetails
                        ls
                        productData={row.product}
                        currency={estimationDetails?.currency}
                        salesOrderStatus={estimationDetails?.status}
                        estimationId={estimationDetails?.estimationId}
                        handleComments={handleComments}
                        canComment={canShowComments}
                        isEstimateView={isEstimatePage}
                        proofDecisionContext={proofDecisionContext}
                        showPerLocationProofApproval={canShowCompleteApprovalPackage}
                        hasCompleteApprovalPackage={Boolean(completeApprovalPackage)}
                        onProofDecisionUpdated={handleProofDecisionUpdated}
                        compactGeneratedSalesOrder={!isEstimatePage}
                        commentSavePending={Boolean(commentStateByProduct[resolveGeneratedEstimateProductIdentityKey(row.product)]?.pending)}
                      />
                    )}
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>

          {!documentRequiresRegeneration && otherServices.length > 0 && <TotalContainer estimationDetails={estimationDetails} otherServices={otherServices} />}

          {!isRenderMode &&
          !documentRequiresRegeneration &&
          !isEstimatePage &&
          !isOrderFirstApprovalFlow &&
          completeApprovalPackage &&
          canShowCompleteApprovalPackage ? (
            <Box sx={{ mt: 3, mb: 3 }}>
              <ArtworkProofPackagePanel
                packageGroup={completeApprovalPackage}
                proofDecisionContext={proofDecisionContext}
                orderId={estimationDetails?.orderId || estimationDetails?.OrderId || null}
                documentType="sales-order"
                documentId={estimationDetails?.estimationId || id}
                onDecisionComplete={handlePackageDecisionCompleted}
              />
            </Box>
          ) : null}

          {!documentRequiresRegeneration ? (
            <>
              {!isRenderMode && canResetApproval && !guidedHasPostApprovalStep ? (
                <Box className={styles.printHidden} sx={{ mt: 2, mb: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    color="error"
                    variant="outlined"
                    size="small"
                    startIcon={<RestartAltIcon fontSize="small" />}
                    onClick={() => setResetApprovalDialogOpen(true)}
                    disabled={resettingApproval}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      borderRadius: 1.5,
                      px: 1.75,
                      py: 0.65,
                      bgcolor: 'rgba(211, 47, 47, 0.035)',
                      borderColor: 'rgba(211, 47, 47, 0.35)',
                      '&:hover': {
                        bgcolor: 'rgba(211, 47, 47, 0.08)',
                        borderColor: 'error.main',
                      },
                    }}
                  >
                    Reset Approval
                  </Button>
                </Box>
              ) : null}
              <Footer
                estimationDetails={estimationDetails}
                onApprove={approveSalesOrder}
                onRequestChange={requestSalesOrderChanges}
                grandTotal={grandTotal}
                totalsCents={totalsCents}
                footerAddress={footerAddress}
                termsHtml={termsHtml}
                isEstimateView={isEstimatePage}
                isRenderMode={isRenderMode}
                artworkProofGate={derivedArtworkProofGate}
                artworkProofRows={proofBundle?.rows || []}
                hasArtworkProofingAttached={hasArtworkProofingAttached}
                onScrollToArtworkProof={scrollToArtworkProof}
                canResetApproval={canResetApproval}
                onResetApproval={() => setResetApprovalDialogOpen(true)}
                resettingApproval={resettingApproval}
                completeArtworkProofPackagePanel={
                  !isRenderMode &&
                  isOrderFirstApprovalFlow &&
                  completeApprovalPackage &&
                  canShowCompleteApprovalPackage ? (
                    <ArtworkProofPackagePanel
                      packageGroup={completeApprovalPackage}
                      proofDecisionContext={proofDecisionContext}
                      orderId={estimationDetails?.orderId || estimationDetails?.OrderId || null}
                      documentType="sales-order"
                      documentId={estimationDetails?.estimationId || id}
                      onDecisionComplete={handlePackageDecisionCompleted}
                      embedded
                    />
                  ) : null
                }
              />
            </>
          ) : null}

          {!isRenderMode && canEdit && estimationDetails && (
            <Box
              className={styles.printHidden}
              sx={{
                position: 'fixed',
                bottom: 16,
                left: 16,
                zIndex: 2000,
              }}
            >
              <Tooltip title="Live Cart">
                <IconButton
                  size="large"
                  aria-label="Open live cart"
                  onClick={() => setCartPanelOpen(true)}
                  sx={{
                    bgcolor: '#1976d2',
                    color: '#fff',
                    border: '1px solid #0f4fa1',
                    borderRadius: '50%',
                    boxShadow: 2,
                    '&:hover': { boxShadow: 4, bgcolor: '#1e88e5' },
                  }}
                >
                  <Badge
                    color="error"
                    badgeContent={Array.isArray(cartOrder?.Products) ? cartOrder.Products.length : 0}
                    max={99}
                  >
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {!isRenderMode && canEdit && estimationDetails && (
          <Drawer
            className={styles.printHidden}
            anchor="right"
            open={cartPanelOpen}
            onClose={() => setCartPanelOpen(false)}
            PaperProps={{ sx: { width: { xs: '100%', sm: 380 } } }}
          >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">Live Cart</Typography>
              <IconButton onClick={() => setCartPanelOpen(false)} size="large">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {Array.isArray(cartOrder?.Products) ? cartOrder.Products.length : 0} item(s)
              </Typography>
              <Box sx={{ maxHeight: '65vh', overflowY: 'auto' }}>
                {(cartOrder?.Products || []).map((p, idx) => {
                  const productName = p?.Name || p?.ProductName || 'Product';
                  const supplier = p?.SupplierName || p?.supplierName || p?.Supplier || p?.supplier;
                  const sku = p?.SKU || p?.ProductCode || p?.asiNumber || p?.ASINumber;
                  const subtitle = [sku ? `${sku} (ASI)` : null, supplier ? `${supplier} (USD)` : null]
                    .filter(Boolean)
                    .join(' • ');
                  const desc = getProductDescription(p);
                  const unitPrice = formatMoney(p?.UnitCost ?? p?.unitPrice ?? p?.Price);
                  const subtotal = formatMoney(p?.Subtotal ?? p?.subTotal ?? p?.subtotal);
                  const total = formatMoney(p?.Total ?? p?.total);
                  const discount = p?.Discount ?? p?.discount;
                  return (
                    <Box key={`${p?.ProductId || p?.Id || 'item'}-${idx}`} sx={{ borderBottom: '1px solid #f1f5f9', py: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{productName}</Typography>
                      {subtitle && <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>{subtitle}</Typography>}
                      {desc && (
                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, mb: 0.5 }}>
                          {String(desc).slice(0, 220)}
                          {String(desc).length > 220 ? '…' : ''}
                        </Typography>
                      )}
                      <Typography variant="caption" sx={{ display: 'block' }}>Qty: {p?.Quantity || 1} units</Typography>
                      <Box sx={{ display: 'flex', gap: 1.25, flexWrap: 'wrap', mt: 0.5, mb: 0.5 }}>
                        {discount !== undefined && discount !== null && <Typography variant="caption">Discount: {discount}%</Typography>}
                        {unitPrice && <Typography variant="caption">Unit: {unitPrice}</Typography>}
                        {subtotal && <Typography variant="caption">Subtotal: {subtotal}</Typography>}
                        {total && <Typography variant="caption" sx={{ fontWeight: 700 }}>Total: {total}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Button size="small" variant="outlined" onClick={() => window.location.assign(buildEditUrl())}>Edit</Button>
                        <Button size="small" variant="outlined" onClick={() => handleCopyCartProduct(idx)}>Copy</Button>
                        <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteCartProduct(idx)}>Delete</Button>
                      </Box>
                    </Box>
                  );
                })}
                {(!cartOrder?.Products || cartOrder.Products.length === 0) && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>No products in cart.</Typography>
                )}
              </Box>
              <Button
                fullWidth
                variant="contained"
                sx={{ mt: 1.5 }}
                onClick={handleRegenerateFromDock}
                disabled={!canRegenerateGeneratedDocument || regeneratingDocument}
              >
                {regeneratingDocument ? 'Regenerating...' : 'Regenerate from cart'}
              </Button>
            </Box>
          </Drawer>
          )}

          {!isRenderMode && canEdit && estimationDetails && (
            <Box
              className={styles.printHidden}
              sx={{
                position: 'fixed',
                bottom: 16,
                left: 72,
                zIndex: 2000,
              }}
            >
              <Tooltip title="Edit sales order">
                <IconButton
                  size="large"
                  aria-label="Edit sales order"
                  onClick={() => window.location.assign(buildEditUrl())}
                  sx={{
                    bgcolor: '#8B0000', // dark red background
                    color: '#fff', // white icon
                    border: '1px solid #600000',
                    borderRadius: '50%',
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 4,
                      bgcolor: '#a00000',
                    },
                  }}
                >
                  <EditIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}

          {!isRenderMode && generatedHistoryAllowed && estimationDetails && (estimationDetails?.orderId || estimationDetails?.OrderId || estimationDetails?.orderID) && (
            <Box
              className={styles.printHidden}
              sx={{ position: 'fixed', bottom: 16, left: canEdit ? 128 : 16, zIndex: 2000 }}
            >
              <Tooltip title={historyFabLabel}>
                <IconButton
                size="large"
                aria-label={historyFabLabel}
                onClick={openSnapshotHistory}
                sx={{
                  bgcolor: '#424242',
                  color: '#fff',
                  border: '1px solid #2e2e2e',
                  borderRadius: '50%',
                  boxShadow: 2,
                  '&:hover': { bgcolor: '#515151' },
                }}
              >
                <HistoryIcon />
              </IconButton>
              </Tooltip>
            </Box>
          )}

          {!isRenderMode && generatedHistoryAllowed ? (
          <Drawer
            className={styles.printHidden}
            anchor="right"
            open={snapshotHistoryOpen}
            onClose={() => setSnapshotHistoryOpen(false)}
            PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}
          >
            <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6">{historyTitle}</Typography>
              <IconButton onClick={() => setSnapshotHistoryOpen(false)} size="large">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider />
            <Box sx={{ p: 2 }}>
              {snapshotHistoryError && <Alert severity="error" sx={{ mb: 2 }}>{snapshotHistoryError}</Alert>}
              {snapshotHistoryLoading ? (
                <EstimationLoading />
              ) : (
                <List dense>
                  {snapshotHistory.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No {isEstimatePage ? 'estimates' : 'sales orders'} found for this order yet.
                    </Typography>
                  )}
                  {snapshotHistory.map((row, idx) => {
                    const revision = Number(row?.revisionNumber || 0) || null;
                    const snapshotDocumentId = row?.documentId || row?.estimationId || row?.id || row?.estimationID;
                    const lifecycle = String(row?.lifecycleStatus || row?.statusLabel || row?.status || '');
                    const isActiveLifecycle = lifecycle.toLowerCase() === 'active';
                    const isCurrent = normalizedCurrentRevision
                      ? revision === normalizedCurrentRevision
                      : (String(snapshotDocumentId) === String(id) && isActiveLifecycle);
                    const dateRaw = row?.cartSnapshotCreatedAt || row?.updatedAt || row?.createdAt;
                    const date = dateRaw ? new Date(dateRaw) : null;
                    const dateStr = date && !Number.isNaN(date.getTime())
                      ? date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
                      : '—';
                    const revisionText = revision ? `Rev ${revision}` : null;
                    const itemCount = Array.isArray(row?.items) ? `${row.items.length} items` : null;
                    const secondary = [revisionText, lifecycle, itemCount, dateStr].filter(Boolean).join(' • ');
                    return (
                      <Box key={`${revision || 'rev'}-${idx}`}>
                        <ListItemButton
                          disabled={isCurrent}
                          onClick={() => {
                            const basePath = isEstimatePage ? `/generated-estimate/${id}` : `/generated-sales-order/${id}`;
                            const to = revision ? `${basePath}?snapshotRevision=${revision}` : basePath;
                            history.push(to);
                            setSnapshotHistoryOpen(false);
                          }}
                        >
                          <ListItemText
                            primary={
                              isCurrent
                                ? (isEstimatePage ? 'Current estimate' : 'Current sales order')
                                : `${isEstimatePage ? 'Estimate' : 'Sales order'} ${revisionText || ''}`.trim()
                            }
                            secondary={secondary}
                          />
                        </ListItemButton>
                        <Divider />
                      </Box>
                    );
                  })}
                </List>
              )}
            </Box>
          </Drawer>
          ) : null}
            </>
          )}
        </Container>
      </Box>
      {!isRenderMode ? <ApprovalRegenerationDialog
        open={approvalRegeneration.open}
        value={approvalRegeneration.mode}
        submitting={regeneratingDocument}
        onChange={(mode) => setApprovalRegeneration((state) => ({ ...state, mode }))}
        onCancel={() => setApprovalRegeneration({ open: false, mode: "reset" })}
        onConfirm={async () => {
          if (regeneratingDocument) return;
          const mode = approvalRegeneration.mode;
          const succeeded = await handleRegenerateFromDock(mode);
          if (succeeded) setApprovalRegeneration({ open: false, mode: "reset" });
        }}
      /> : null}
      {!isRenderMode ? <DecorationRoutingErrorDialog
        open={decorationRoutingError.open}
        issues={decorationRoutingError.issues}
        onClose={() => setDecorationRoutingError({ open: false, issues: [] })}
        onOpenCart={() => {
          setCartPanelOpen(true);
          setDecorationRoutingError({ open: false, issues: [] });
        }}
      /> : null}
      {!isRenderMode ? <ResetSalesOrderApprovalDialog
        open={resetApprovalDialogOpen}
        submitting={resettingApproval}
        onCancel={() => {
          if (resettingApproval) return;
          setResetApprovalDialogOpen(false);
        }}
        onConfirm={handleResetSalesOrderApproval}
      /> : null}
    </>
  );
};

export default GeneratedSalesOrder;
